import { HubConnectionState } from '@microsoft/signalr';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAppContext } from '../AppContext';
import { useClientLogContext } from '../ClientLogContext';
import { POSSignalRHub } from '../SignalRConnection';

export type IListingNotificationContext = {
  hubConnection?: POSSignalRHub;
};

export const ListingNotificationContext =
  createContext<IListingNotificationContext>({
    hubConnection: undefined,
  });

export const useListingNotificationContext = () =>
  useContext(ListingNotificationContext);

export function ListingNotificationContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { activeAccountWebClientConfig } = useAppContext();
  const [hubConnection, setHubConnection] = useState<POSSignalRHub>();
  const hubConnectionRef = useRef<POSSignalRHub | undefined>(hubConnection);
  hubConnectionRef.current = hubConnection;
  const { logEvent } = useClientLogContext();

  useEffect(() => {
    // Only attempt to connect to signal R when there is a login
    if (activeAccountWebClientConfig.activeAccountId) {
      const newHubConnection = new POSSignalRHub(
        '/hubs/listingnotification',
        activeAccountWebClientConfig,
        logEvent
      );

      newHubConnection
        .start()
        .then(() => {
          setHubConnection(newHubConnection);
          console.debug(
            `ListingNotification connection ready with id: ${newHubConnection.connectionId}`
          );
        })
        .catch((err) => {
          setHubConnection(undefined);
          console.warn(
            'ListingNotification could not be started at this time: ' + err
          );
        });

      newHubConnection.onclose((err) => {
        setHubConnection(undefined);
        console.debug('ListingNotification connection closed', err);
      });
    }

    return () => {
      const conn = hubConnectionRef.current;
      if (
        conn &&
        !(
          conn.state === HubConnectionState.Disconnected ||
          conn.state === HubConnectionState.Disconnecting
        )
      ) {
        console.debug(
          `Stopping ListingNotification connection (state: ${conn.state}) with id: ${conn.connectionId}`
        );
        conn.stop();
      }
      setHubConnection(undefined);
    };
  }, [activeAccountWebClientConfig, logEvent]);

  return (
    <ListingNotificationContext.Provider
      value={{
        hubConnection,
      }}
    >
      {children}
    </ListingNotificationContext.Provider>
  );
}
